/**
 * "Setting up your book" — getting clients, properties and parcels into the app.
 */

import type { HelpArticle } from '../types';

export const setupArticles: HelpArticle[] = [
  {
    slug: 'adding-a-client',
    title: 'Adding a new client',
    summary: 'Start here when a new customer signs with your firm. The client record is what everything else hangs off.',
    category: 'setup',
    keywords: ['new client', 'add client', 'create client', 'customer', 'sign up', 'wizard', 'onboard', 'short name'],
    related: ['the-big-picture', 'adding-a-property', 'fee-agreements'],
    body: [
      {
        kind: 'text',
        text: 'A client is the account that pays you. Create the client first — you cannot add their properties until it exists.',
      },
      {
        kind: 'steps',
        intro: 'To add one:',
        steps: [
          'Click "+ New Client" in the Setup section of the left menu.',
          'Enter the legal name exactly as it appears on the signed agreement. This is what goes on invoices.',
          'Enter a short name. This is a nickname used in lists and dropdowns where the full legal name is too long to read.',
          'Set the status to active.',
          'Add the fee terms if you already have them. You can come back and do this later, but you cannot invoice the client until they exist.',
          'Save.',
        ],
      },
      {
        kind: 'shot',
        src: '/help-shots/clients-list.png',
        alt: 'The Clients list screen, showing a table of client names with columns for status, number of properties and assigned manager.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Pick the short name carefully',
        text: 'It is required, and you will read it a hundred times a week. "Bluebonnet" is a good short name. "BPG LLC #2 (TX)" is not.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Nearly identical names are usually not duplicates',
        text: 'Real estate investors deliberately hold each building in a separately named company — "Carroll Family Investments Ltd", "Carroll Family Investments LP" and "Carroll Family Investments #2 Ltd" may be three genuinely different clients with three different agreements. Never merge them because they look alike. Ask somebody who knows the account.',
      },
      {
        kind: 'heading',
        text: 'Assigned manager',
      },
      {
        kind: 'text',
        text: 'This is the person at your firm who owns the relationship. It matters more than it looks: it flows down to every property and parcel under the client, and it is what decides whose dashboard this work shows up on.',
      },
    ],
  },

  {
    slug: 'adding-a-property',
    title: 'Adding a property',
    summary: 'A property is a real address. Add it under the client who owns it.',
    category: 'setup',
    keywords: ['new property', 'add property', 'create property', 'building', 'address', 'site', 'location'],
    related: ['adding-a-client', 'adding-a-parcel', 'the-big-picture'],
    body: [
      {
        kind: 'text',
        text: 'A property is the thing with a street address. It belongs to exactly one client.',
      },
      {
        kind: 'steps',
        steps: [
          'Click "+ New Property" in the Setup section of the left menu.',
          'Pick the client who owns it.',
          'Type the street address. The address box suggests real addresses as you type — pick from the list rather than typing it all out, so the spelling matches the government records.',
          'Choose the property type, for example office, retail or industrial.',
          'Save.',
        ],
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'A property on its own does nothing',
        text: 'You cannot appeal a property. You appeal the parcels underneath it. A property with no parcels will sit in "Properties in setup" on the dashboard until you add at least one.',
      },
    ],
  },

  {
    slug: 'adding-a-parcel',
    title: 'Adding a parcel',
    summary: 'The parcel carries the government’s ID number and the values you will argue about. This is the record that does the real work.',
    category: 'setup',
    keywords: ['new parcel', 'add parcel', 'create parcel', 'account number', 'parcel number', 'pin', 'apn', 'legal description'],
    related: ['adding-a-property', 'what-is-a-parcel-value', 'the-big-picture'],
    body: [
      {
        kind: 'steps',
        steps: [
          'Click "+ New Parcel" in the Setup section of the left menu.',
          'Pick the property it sits on.',
          'Enter the parcel or account number exactly as the government writes it, including any leading zeros, dashes and spaces.',
          'Choose the assessor — the office that sets this parcel’s value.',
          'Add the jurisdictions that tax it, and the tax rate for each.',
          'Add this year’s value from the notice, if you have it.',
          'Save.',
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Copy the account number character for character',
        text: 'This number is how your record is matched to the government’s record. A dropped leading zero or a stray space means bills and values quietly attach to nothing. If you are pasting from a spreadsheet, check that long numbers have not been rounded — spreadsheets do that silently to numbers above about sixteen digits.',
      },
      {
        kind: 'shot',
        src: '/help-shots/parcel-detail.png',
        alt: 'A parcel detail screen with tabs across the top for values, appeals, tax records and documents, and the parcel identification details listed on the left.',
      },
      {
        kind: 'heading',
        text: 'Real estate or personal property?',
      },
      {
        kind: 'text',
        text: 'Most parcels are real estate — land and buildings. Some are business personal property, which means the equipment, fixtures and inventory a business keeps inside a building. Those are taxed separately, follow different deadlines, and are handled through Returns rather than the normal appeal path.',
      },
    ],
  },

  {
    slug: 'importing-from-a-spreadsheet',
    title: 'Importing a lot of records at once',
    summary: 'Bringing a whole book of business in from a spreadsheet, and how to check it landed correctly before you commit it.',
    category: 'setup',
    keywords: ['import', 'upload', 'spreadsheet', 'excel', 'csv', 'bulk', 'migration', 'batch', 'mapping', 'columns'],
    related: ['adding-a-parcel', 'what-is-a-parcel-value'],
    body: [
      {
        kind: 'text',
        text: 'When a new firm or a big client joins, you will not type in a thousand parcels by hand. The Import screen takes a spreadsheet and creates the records for you.',
      },
      {
        kind: 'steps',
        steps: [
          'Go to Import in the System section of the left menu.',
          'Upload your file.',
          'Match each column in your spreadsheet to a field in the app. The app guesses, but check every guess.',
          'Run the preview. Nothing is saved yet at this point.',
          'Read the preview carefully — see below.',
          'If it looks right, commit the import. If it does not, fix the spreadsheet and start again.',
        ],
      },
      {
        kind: 'shot',
        src: '/help-shots/import-wizard.png',
        alt: 'The import wizard showing a list of spreadsheet columns on the left matched to app fields on the right, with a preview of the rows to be created underneath.',
      },
      {
        kind: 'heading',
        text: 'What to check in the preview, every time',
      },
      {
        kind: 'bullets',
        items: [
          'The number of rows. If your file has 1,204 parcels and the preview says 1,198, find the six before you continue.',
          'Long account numbers. Check a few of the longest ones against the source file, digit for digit.',
          'Percentages. A fee rate of 20% is sometimes stored as 0.2 and sometimes as 20. If it imports as 0.2%, every invoice will be a hundred times too small.',
          'Blank values. Make sure blanks came through as blank, not as zero.',
          'Totals rows. Spreadsheets often have a "Total" line at the bottom. That is not a parcel. It must not import as one.',
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Importing the same file twice can duplicate your whole book',
        text: 'The import matches on a key column so it can update existing records instead of duplicating them. If that key is wrong — for instance if your source file uses a different ID each year — a second import creates a second copy of everything. Check with an admin before re-importing a file you have already used.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Import in small batches first',
        text: 'Load ten rows, look at the result in the app, then load the rest. Ten wrong rows take ten minutes to clean up. A thousand wrong rows take a week.',
      },
    ],
  },

  {
    slug: 'parent-clients',
    title: 'Parent clients, for investors who own several companies',
    summary: 'Group several client accounts under one owner so you can report on all of them together.',
    category: 'setup',
    keywords: ['parent client', 'group', 'holding company', 'fund', 'portfolio', 'rollup', 'roll up', 'investor', 'consolidated'],
    related: ['adding-a-client', 'the-big-picture', 'reports-overview'],
    body: [
      {
        kind: 'text',
        text: 'A big investor often holds each building in its own company for legal reasons. To you that is ten clients. To them it is one portfolio, and they want one report covering all of it.',
      },
      {
        kind: 'text',
        text: 'A parent client is that grouping. It does not replace the individual clients — each of those still has its own agreement and its own invoices. The parent just sits above them so you can total things up.',
      },
      {
        kind: 'steps',
        intro: 'To set one up:',
        steps: [
          'Go to Parent Clients in the Portfolio section of the left menu.',
          'Create the parent and give it the investor’s name.',
          'Use "Manage members" to add each client company that belongs to it.',
        ],
      },
      {
        kind: 'callout',
        tone: 'note',
        text: 'Invoices are still raised against the individual client, not the parent. The parent is for reporting, not for billing.',
      },
    ],
  },
];
